import actionTypes from './actionTypes';
import GraphActions from '../graph';
import GraphSource from '../../sources/graph';

import { addNotification } from './notification';
import { assocPath } from 'ramda';
import { setDraggingOverGroupElementID } from './graphEditor';
import { getParentOffset, getParents } from '../../components/graph/utils';
import { MAP_ELEMENT_TYPES } from '../../constants';
import { safeToLower } from '../../utils/core';

export const dismissMapElementConfig = flowId => ({
    type: actionTypes.MAP_ELEMENT_CONFIGURATION_DISMISS,
    payload: flowId,
});

export const setMapElement = mapElement => ({
    type: actionTypes.MAP_ELEMENT_SET,
    payload: mapElement,
});

// This is used when the user is dragging a new outcome around and hasn't let go
export const addFakeOutcomeToXY = ({ fromMapElementId, toXY }) => ({
    type: actionTypes.MAP_ELEMENT_CREATE_FAKE_OUTCOME_TO_XY,
    payload: {
        fromMapElementId,
        toXY,
        type: 'new',
    },
});

// This is used by openOutcomeConfig and is used when the config panel is open
// and we still want to render the pending outcome in the background
export const addFakeOutcomeToMapElement = ({ fromMapElementId, toMapElementId }) => ({
    type: actionTypes.MAP_ELEMENT_CREATE_FAKE_OUTCOME_TO_MAP_ELEMENT,
    payload: {
        fromMapElementId,
        toMapElementId,
        type: 'pending',
    },
});

export const removeAllFakeOutcomes = ({ type }) => ({
    type: actionTypes.MAP_ELEMENT_REMOVE_ALL_OUTCOME,
    payload: { type },
});

export const removeFakeOutcome = ({ mapElementId, type }) => ({
    type: actionTypes.MAP_ELEMENT_REMOVE_OUTCOME,
    payload: { mapElementId, type },
});

// This is used when the user has let go of an outcome on a map element
export const openOutcomeConfig = ({ mapElementId, config, openConfig }) => async (dispatch, getState) => {
    const state = getState();

    if (state.graphEditor.hoveringMapElementId) {
        // Open the new Outcome config panel
        const callbacks = config.callbacks ? config.callbacks : [];
        callbacks.push({ execute: () => dispatch(removeFakeOutcome({ mapElementId, type: 'pending' })), type: 'done' });
        config.callbacks = callbacks;
        const targetElement = state.mapElements.find(e => e.id === state.graphEditor.hoveringMapElementId);
        if (safeToLower(targetElement.elementType) !== MAP_ELEMENT_TYPES.note) {
            config.inputs.nextMapElementId = state.graphEditor.hoveringMapElementId;
            openConfig(config.flow, config.inputs, config.callbacks, config.options, config.mapElementId, config.element);
            // Render a fake outcome line
            dispatch(addFakeOutcomeToMapElement({
                fromMapElementId: mapElementId,
                toMapElementId: state.graphEditor.hoveringMapElementId,
            }));
        }
    }
};

export const setMapElementDeveloperName = ({ mapElement, developerName }) => ({
    type: actionTypes.MAP_ELEMENT_SET_DEVELOPER_NAME,
    payload: {
        mapElement,
        developerName,
    },
});

export const removeMapElement = mapElementId => ({
    type: actionTypes.MAP_ELEMENT_REMOVE,
    payload: mapElementId,
});

export const setGroupElements = (groupElementData, flowId) => async (dispatch, getState) => {
    const state = getState();
    // Remove all group elements in this flow before adding updated group elements
    const allGroups = state.groupElements.filter(el => state.groupElementIdFlowId[el.id] !== flowId);
    // Set all the groupElements for all flows
    dispatch({
        type: actionTypes.GROUP_ELEMENT_SET_ALL,
        payload: [...allGroups, ...groupElementData],
    });
    // Set all the groupElementIdFlowId references for this flow
    dispatch({
        type: actionTypes.GROUP_ELEMENT_ID_FLOW_ID_SET,
        payload: { groupElementData, flowId },
    });
}

export const setAllMapElements = (mapElements, flowId) => async (dispatch, getState) => {
    const state = getState();
    // Remove all map elements in this flow before adding updated map elements
    const allMapElements = state.mapElements.filter(el => state.mapElementIdFlowId[el.id] !== flowId);
    // Set all the mapElements for all flows
    dispatch({
        type: actionTypes.MAP_ELEMENT_SET_ALL,
        payload: [...allMapElements, ...mapElements],
    });
    // Set all the mapElementIdFlowId references for this flow
    dispatch({
        type: actionTypes.MAP_ELEMENT_ID_FLOW_ID_SET,
        payload: { mapElements, flowId },
    });
}

export const moveMapElement = ({ mapElementId, x, y }) => ({
    type: actionTypes.MAP_ELEMENT_MOVE,
    payload: {
        mapElementId,
        x,
        y,
    },
});

export const deleteMapElementFlowIdAssoc = id => ({
    type: actionTypes.MAP_ELEMENT_DELETE,
    payload: id,
});

export const openFlow = ({
    flowId,
    editingToken,
    tenantId,
    authenticationToken,
}) => async (dispatch) => {

    try {

        const groupElements = await GraphSource.getGroupElements(
            flowId,
            editingToken,
            tenantId,
            authenticationToken,
        );

        const allMapElementData = await GraphSource.getMapElements(
            flowId,
            editingToken,
            tenantId,
            authenticationToken,
        );
            
        dispatch(setGroupElements(groupElements, flowId));
        dispatch(setAllMapElements(allMapElementData, flowId));

    } catch (error) {
        dispatch(addNotification({
            type: 'error',
            message: error.message,
            isPersistent: true,
        }));
    }
};

export const fetchMapElement = ({
    mapElementId,
    flowId,
    editingToken,
    tenantId,
    authenticationToken,
}) => async (dispatch) => {

    try {

        const mapElementData = await GraphSource.getMapElement(
            mapElementId,
            flowId,
            editingToken,
            'map',
            tenantId,
            authenticationToken,
        );

        dispatch(setMapElement(mapElementData));

    } catch (error) {
        dispatch(addNotification({
            type: 'error',
            message: error.message,
            isPersistent: true,
        }));
    }
};

export const saveMapElement = (
    payload,
    flowId,
    editingToken,
    tenantId,
    authenticationToken,
    cancel,
) => async (dispatch) => {

    try {
        const mapElementData = await GraphSource.saveMapElement(
            payload,
            flowId,
            editingToken,
            'map',
            tenantId,
            authenticationToken,
        );

        GraphActions.get(flowId, tenantId, authenticationToken);

        dispatch(
            setMapElement(mapElementData),
        );

        cancel(flowId);

    } catch (error) {
        dispatch(addNotification({
            type: 'error',
            message: error.message,
            isPersistent: true,
        }));
    }
};

export const deleteMapElement = (
    id,
    flowId,
    editingToken,
    tenantId,
    authenticationToken,
) => async (dispatch) => {

    try {
        await GraphSource.deleteMapElement(
            id,
            flowId,
            editingToken,
            'map',
            tenantId,
            authenticationToken,
        );

        dispatch(deleteMapElementFlowIdAssoc(id));

    } catch (error) {
        dispatch(addNotification({
            type: 'error',
            message: error.message,
            isPersistent: true,
        }));
    }
};

export const saveSubFlowMapElement = (
    payload,
    flowId,
    editingToken,
    tenantId,
    authenticationToken,
    cancel,
    selectors,
) => (dispatch) => {

    /**
     * Generating an array of arguments that will be passed to the subflow.
     * This will look something like:

        [
            {
                valueElementInSubflowId: {
                    id: 0000-0000-0000-0000-0000,
                },
                valueElementToApplyId: {
                    id: 0000-0000-0000-0000-0000,
                    typeElementPropertyId: 0000-0000-0000-0000-0000,
                }
            }
        ]

      * If we consider the subflow as a function, the valueElementInSubflowId key
      * described above represents a single formal argument
      * (in the context of a subflow, this being a value)
      * that the function can accept.
      * This will be a value that has already been imported into the subflow.
      *
      * The valueElementToApplyId is the actual argument that the parent flow is passing
      * to the subflow. This value needs to have already been imported into the parent flow.
     */
    const subFlowArguments = selectors ? selectors.reduce((acc, selector) => {
        if (selector.selectedValueMeta) {
            acc.push({
                valueElementInSubflowId: {
                    id: selector.formalValueId || null,
                },
                valueElementToApplyId: {
                    id: selector.selectedValueMeta.id || null,
                    typeElementPropertyId: selector.selectedValueMeta.propertyId || null,
                },
            });
        }
        return acc;
    }, []) : null;

    const payloadWithArguments = assocPath(['subflow', 'arguments'], subFlowArguments, payload);

    dispatch(
        saveMapElement(
            payloadWithArguments,
            flowId,
            editingToken,
            tenantId,
            authenticationToken,
            cancel,
        ),
    );
};

/**
 * @param {Object} mapElement Metadata describing a subflow map element
 * @param {String} subflowId Flow ID for Flow selected to be run as a subflow
 *
 * @description Sets the map elements subflow ID
 */
export const setMapElementSubflowId = ({
    mapElement, subflowId,
}) => async (dispatch) => {

    dispatch({
        type: actionTypes.MAP_ELEMENT_SET_SUBFLOW_ID,
        payload: {
            mapElement,
            subflowId,
        },
    });

};

export const endMoveMapElement = ({ id, x, y, editingToken }) => async (dispatch, getState) => {
    const state = getState();

    const flowId = state.mapElementIdFlowId[id];
    const mapElement = state.mapElements.find(ge => ge.id === id);
    const updatedMapElement = { ...mapElement, x, y, groupElementId: state.graphEditor.draggingOverGroupElementId };
    dispatch(setDraggingOverGroupElementID(null));

    if (updatedMapElement.groupElementId !== mapElement.groupElementId) {
        const previousParentOffset = getParentOffset(getParents(mapElement, state.groupElements));
        const newParentOffset = getParentOffset(getParents(updatedMapElement, state.groupElements));

        updatedMapElement.x = updatedMapElement.x + previousParentOffset.x - newParentOffset.x;
        updatedMapElement.y = updatedMapElement.y + previousParentOffset.y - newParentOffset.y;
    }

    dispatch(
        saveMapElement(
            updatedMapElement,
            flowId,
            editingToken,
            state.currentUser.tenantId,
            state.currentUser.authenticationToken,
            () => {},
        ),
    );
};
